/* ============================================================
   GABBIANO CUSTOM — Product card image arrows
   ------------------------------------------------------------
   Adds prev/next arrows to product cards so visitors can browse
   all product images without leaving the collection page.

   Self-contained: reads the product handle from the theme's own
   <product-card handle="..."> element and fetches image data from
   /products/<handle>.js on demand. No Liquid changes required,
   which keeps this safe across theme updates.

   5 Oct 2026: aware of model view (feature 8). With
   html.gb-model-view the visible image is flipped (model photo
   at rest, packshot on hover), so the starting index flips too.
   ============================================================ */
(function () {
  'use strict';

  var CACHE = {};
  var HOVER_CAPABLE = !(window.matchMedia && window.matchMedia('(hover: none)').matches);

  var LABELS = {
    nl: { prev: 'Vorige afbeelding', next: 'Volgende afbeelding' },
    en: { prev: 'Previous image', next: 'Next image' },
    de: { prev: 'Vorheriges Bild', next: 'Nächstes Bild' },
    fr: { prev: 'Image précédente', next: 'Image suivante' }
  };
  var lang = (document.documentElement.lang || 'en').slice(0, 2).toLowerCase();
  var T = LABELS[lang] || LABELS.en;

  var ICON = {
    prev: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="15 18 9 12 15 6"></polyline></svg>',
    next: '<svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round" aria-hidden="true" focusable="false"><polyline points="9 18 15 12 9 6"></polyline></svg>'
  };

  /* Reduce a CDN url to a comparable filename, ignoring size suffixes. */
  function fileKey(url) {
    if (!url) return '';
    var name = String(url).split('?')[0].split('/').pop();
    return name.replace(/_(?:\d+x\d*|x\d+)(?:_crop_[a-z]+)?(?:@\d+x)?(?=\.[a-z0-9]+$)/i, '').toLowerCase();
  }

  function indexOfImage(images, url) {
    var key = fileKey(url);
    if (!key) return -1;
    for (var i = 0; i < images.length; i++) {
      if (fileKey(images[i]) === key) return i;
    }
    return -1;
  }

  function sized(url, width) {
    var clean = String(url).indexOf('//') === 0 ? 'https:' + url : String(url);
    return clean + (clean.indexOf('?') === -1 ? '?' : '&') + 'width=' + width;
  }

  function getImages(handle) {
    if (CACHE[handle]) return CACHE[handle];

    var root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

    CACHE[handle] = fetch(root + 'products/' + handle + '.js')
      .then(function (response) { return response.ok ? response.json() : null; })
      .then(function (product) {
        return product && Array.isArray(product.images) ? product.images : [];
      })
      .catch(function () { return []; });

    return CACHE[handle];
  }

  function makeArrow(direction) {
    var button = document.createElement('button');
    button.type = 'button';
    button.className = 'product-card__arrow product-card__arrow--' + direction;
    button.setAttribute('data-card-arrow', direction);
    button.setAttribute('aria-label', T[direction]);
    button.innerHTML = ICON[direction];
    return button;
  }

  function addArrows(figure) {
    if (figure.querySelector('[data-card-arrow]')) return;
    figure.appendChild(makeArrow('prev'));
    figure.appendChild(makeArrow('next'));
  }

  /* Arrows are only meaningful when a product has more than one image.
     The theme renders a second <img> for its hover effect, which is a
     cheap and reliable signal that at least two images exist. */
  function setup(scope) {
    var cards = (scope || document).querySelectorAll('product-card[handle]');

    cards.forEach(function (card) {
      var figure = card.querySelector('.product-card__figure');
      if (!figure) return;
      if (!figure.querySelector('.product-card__image--secondary')) return;
      addArrows(figure);
    });
  }

  /* Where is the visitor right now, before this click?

     On a hover-capable device the arrows are only reachable by hovering the
     card, and hovering shows the theme's secondary image. So the starting
     point is the secondary image, NOT the featured one — otherwise the first
     next-click would land on an image that is already on screen and appear
     to do nothing. We locate the secondary image in the list by filename and
     fall back to position 1, which is where the theme takes it from.

     Model view (html.gb-model-view) flips this: the secondary image is shown
     at rest (touch) and the primary on hover (mouse). */
  function startIndex(link, images) {
    if (link.dataset.cardIndex !== undefined) {
      return parseInt(link.dataset.cardIndex, 10) || 0;
    }

    var secondary = link.querySelector('.product-card__image--secondary');
    var modelView = document.documentElement.classList.contains('gb-model-view');
    var showingSecondary = modelView ? !HOVER_CAPABLE : HOVER_CAPABLE;

    if (secondary && showingSecondary) {
      var found = indexOfImage(images, secondary.getAttribute('src') || secondary.currentSrc || secondary.src);
      return found === -1 ? 1 : found;
    }

    var primary = link.querySelector('.product-card__image--primary');

    if (primary) {
      var start = indexOfImage(images, primary.getAttribute('src') || primary.currentSrc || primary.src);
      if (start !== -1) return start;
    }

    return 0;
  }

  function navigate(figure, direction) {
    var card = figure.closest('product-card[handle]');
    var link = figure.querySelector('.product-card__media');
    if (!card || !link) return;

    var handle = card.getAttribute('handle');
    if (!handle) return;

    getImages(handle).then(function (images) {
      if (images.length < 2) return;

      var image = link.querySelector('.product-card__image--primary');
      if (!image) return;

      var index = startIndex(link, images);
      index = (index + direction + images.length) % images.length;

      link.dataset.cardIndex = String(index);
      link.classList.add('product-card__media--browsing');

      image.removeAttribute('srcset');
      image.removeAttribute('sizes');
      image.setAttribute('src', sized(images[index], 900));

      /* Warm up the next image in the same direction */
      var ahead = new Image();
      ahead.src = sized(images[(index + direction + images.length) % images.length], 900);
    });
  }

  /* Prefetch on hover so the first click feels instant */
  document.addEventListener(
    'pointerenter',
    function (event) {
      var target = event.target;
      if (!target || !target.closest) return;
      var card = target.closest('product-card[handle]');
      if (card) getImages(card.getAttribute('handle'));
    },
    true
  );

  document.addEventListener(
    'click',
    function (event) {
      var button = event.target.closest && event.target.closest('[data-card-arrow]');
      if (!button) return;

      event.preventDefault();
      event.stopPropagation();

      var figure = button.closest('.product-card__figure');
      if (figure) {
        navigate(figure, button.getAttribute('data-card-arrow') === 'next' ? 1 : -1);
      }
    },
    true
  );

  /* Initial pass plus re-runs after filtering, pagination or section reloads */
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () { setup(); });
  } else {
    setup();
  }

  var observer = new MutationObserver(function (mutations) {
    for (var i = 0; i < mutations.length; i++) {
      if (mutations[i].addedNodes.length) { setup(); return; }
    }
  });

  observer.observe(document.documentElement, { childList: true, subtree: true });
})();
